import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { branchesAPI, categoriesAPI, paymentMethodsAPI, posAPI, usersAPI } from "@/lib/api";
import { usePermission } from "@/hooks/usePermission";
import {
	filtersFromSearchParams,
	filtersToSearchParams,
	type ReportFilterField,
	type ReportFiltersState,
} from "../types";

/**
 * Report filters live in the URL (?period=…&branch=…), so a report can be bookmarked or shared, survives a
 * reload, and keeps its filters when moving between the reports home and a report.
 */
export function useReportFilters() {
	const [searchParams, setSearchParams] = useSearchParams();
	const filters = useMemo(() => filtersFromSearchParams(searchParams), [searchParams]);
	const setFilters = useCallback(
		(next: ReportFiltersState) => setSearchParams(prev => filtersToSearchParams(next, prev), { replace: true }),
		[setSearchParams],
	);
	/** Current filters as a query string ("?period=…"), for links that should carry them. */
	const search = useMemo(() => {
		const qs = filtersToSearchParams(filters).toString();
		return qs ? `?${qs}` : "";
	}, [filters]);
	return { filters, setFilters, search };
}

export interface FilterOption {
	value: string;
	label: string;
}

const asList = (data: unknown, key?: string): any[] => {
	if (Array.isArray(data)) return data;
	const obj = data as Record<string, unknown> | undefined;
	if (key && Array.isArray(obj?.[key])) return obj?.[key] as any[];
	return Array.isArray(obj?.data) ? (obj?.data as any[]) : [];
};

/**
 * Options for the filter selects. Each list loads only when the report shows that filter and the user may
 * read it (no 403s for limited roles); a filter without options is hidden.
 */
export function useFilterOptions(fields: ReportFilterField[], branchId: string) {
	const canBranches = usePermission("branches.read");
	const canPos = usePermission("pos.read");
	const canUsers = usePermission("users.read");
	const canCategories = usePermission("categories.read");
	const canPaymentMethods = usePermission("paymentMethods.read");
	const wants = (f: ReportFilterField) => fields.includes(f);

	const branches = useQuery({
		queryKey: ["branches"],
		queryFn: () => branchesAPI.getAll(),
		enabled: wants("branch") && canBranches,
		staleTime: 5 * 60 * 1000,
	});
	const pos = useQuery({
		queryKey: ["reports-filter-pos", branchId],
		queryFn: () => posAPI.getAll({ limit: 100, branchId: branchId || undefined }),
		enabled: wants("pos") && canPos,
		staleTime: 5 * 60 * 1000,
	});
	const users = useQuery({
		queryKey: ["reports-filter-users", branchId],
		queryFn: () => usersAPI.getAll({ limit: 100, branchId: branchId || undefined }),
		enabled: wants("employee") && canUsers,
		staleTime: 5 * 60 * 1000,
	});
	const categories = useQuery({
		queryKey: ["reports-filter-categories"],
		queryFn: () => categoriesAPI.getAll({ limit: 100 }),
		enabled: wants("category") && canCategories,
		staleTime: 5 * 60 * 1000,
	});
	const paymentMethods = useQuery({
		queryKey: ["payment-methods"],
		queryFn: () => paymentMethodsAPI.getAll(),
		enabled: wants("paymentMethod") && canPaymentMethods,
		staleTime: 5 * 60 * 1000,
	});

	return useMemo(
		() => ({
			branch: asList(branches.data).map((b: any): FilterOption => ({
				value: String(b.id),
				label: b.name || b.nameEn || String(b.id),
			})),
			pos: asList(pos.data, "pos").map((p: any): FilterOption => ({
				value: String(p.id),
				label: p.name || p.code || String(p.id),
			})),
			employee: asList(users.data, "users").map((u: any): FilterOption => ({
				value: String(u.id),
				label: [u.firstName, u.lastName].filter(Boolean).join(" ") || u.fullName || String(u.id),
			})),
			category: asList(categories.data, "categories").map((c: any): FilterOption => ({
				value: String(c.id),
				label: c.name || String(c.id),
			})),
			/** Codes only; the bar translates them with paymentMethodLabel. */
			paymentMethodCodes: asList(paymentMethods.data)
				.filter((m: any) => m?.code && m.isActive !== false)
				.map((m: any) => String(m.code)),
		}),
		[branches.data, pos.data, users.data, categories.data, paymentMethods.data],
	);
}
